import { Fragment, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { OpeningImportResultDto } from "@hisense/shared";
import { useReceiptWarehouses } from "@/api/warehouseReceipts";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { parseOpeningSheet, type OpeningSheetRow } from "@/lib/openingSheet";
import { formatQty } from "@/domain/money";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { vnDate } from "@hisense/shared";

const today = () => vnDate();

/**
 * Nhập tồn đầu kỳ từ phiếu đếm Excel (18/09/2026: Kho & LOT là nguồn tồn duy nhất).
 *
 * Đọc tệp ngay trên trình duyệt, gửi các dòng cho máy chủ xem trước (không ghi gì), hiện từng dòng sẵn sàng /
 * lỗi, rồi mới cho lập phiếu. Còn dòng lỗi thì nút lập phiếu khoá — sửa trong Excel rồi tải lại, để tệp Excel
 * luôn là bản đúng cuối cùng người đếm đã ký. Phiếu lập ra là nháp; nộp và ghi sổ ở danh sách bên dưới.
 */
export function OpeningImportPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const warehouses = useReceiptWarehouses();
  const [warehouseId, setWarehouseId] = useState("");
  const [countedDate, setCountedDate] = useState(today());
  const [reason, setReason] = useState(() => t("openingImport.reasonDefault"));
  const [rows, setRows] = useState<OpeningSheetRow[] | null>(null);
  const [sheet, setSheet] = useState("");
  const [report, setReport] = useState<OpeningImportResultDto | null>(null);
  const [busy, setBusy] = useState<"reading" | "checking" | "creating" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  // Đã lập phiếu cho lần tải này: khoá nút lập cho tới khi chọn lại tệp (lần nhập mới).
  const [done, setDone] = useState(false);
  // Một lần nhập = một requestId: bấm lập phiếu hai lần (hoặc mạng chập chờn) không ra hai bộ phiếu. Đổi kho, ngày
  // hay lý do là nội dung khác nên lấy requestId mới — máy chủ từ chối requestId cũ đi với nội dung khác.
  const requestId = useRef(crypto.randomUUID());
  const changed = () => { requestId.current = crypto.randomUUID(); setReport(null); setNotice(null); };

  // Mặc định kho Sài Gòn (anh Toàn chốt 18/09/2026: 62 mã tồn tạm để WH-SG), không có thì kho đầu tiên.
  useEffect(() => {
    const list = warehouses.data?.data ?? [];
    if (!warehouseId && list.length) setWarehouseId((list.find((w) => w.code === "WH-SG") ?? list[0]!).id);
  }, [warehouses.data, warehouseId]);

  const check = async (lines: OpeningSheetRow[], id = requestId.current) => {
    setBusy("checking");
    try {
      const res = await apiFetch<{ data: OpeningImportResultDto }>("/stock-moves/counts/import", {
        method: "POST",
        body: { requestId: id, dryRun: true, defaultWarehouseId: warehouseId, countedDate, reason, rows: lines },
      });
      setReport(res.data);
    } catch (e) {
      setError(getLocalizedError(e, t, "stockCounts.error"));
    } finally {
      setBusy(null);
    }
  };

  const onFile = async (file: File | undefined) => {
    setError(null); setNotice(null); setReport(null); setRows(null); setDone(false);
    setFileName(file?.name ?? "");
    if (!file) return;
    setBusy("reading");
    requestId.current = crypto.randomUUID();
    try {
      const parsed = await parseOpeningSheet(await file.arrayBuffer());
      setRows(parsed.rows);
      setSheet(parsed.sheet);
      await check(parsed.rows);
    } catch {
      setError(t("openingImport.readError"));
      setBusy(null);
    }
  };

  const create = async () => {
    if (!rows || busy) return;
    setBusy("creating"); setError(null);
    try {
      const res = await apiFetch<{ data: OpeningImportResultDto }>("/stock-moves/counts/import", {
        method: "POST",
        body: { requestId: requestId.current, dryRun: false, defaultWarehouseId: warehouseId, countedDate, reason, rows },
      });
      setReport(res.data);
      setDone(true);
      setNotice(t("openingImport.created", { docs: res.data.documents.map((d) => `${d.documentNo} · ${d.warehouseName} · ${d.lines}`).join("; ") }));
      await qc.invalidateQueries({ queryKey: ["stock-counts"] });
    } catch (e) {
      setError(getLocalizedError(e, t, "stockCounts.error"));
    } finally {
      setBusy(null);
    }
  };

  const ready = !done && !!report && report.errors === 0 && report.ok > 0 && report.documents.length === 0;
  return (
    <section className="my-4 space-y-3 rounded border border-line bg-surface p-4">
      <div>
        <h2 className="text-base font-semibold text-ink">{t("openingImport.title")}</h2>
        <p className="mt-1 max-w-3xl text-xs text-ink-soft">{t("openingImport.intro")}</p>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <div>
          <FieldLabel htmlFor="opi-wh">{t("openingImport.warehouse")}</FieldLabel>
          <Select id="opi-wh" containerClassName="w-full" value={warehouseId} disabled={!!busy} onChange={(e) => { setWarehouseId(e.target.value); changed(); }}>
            {warehouses.data?.data.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="opi-date">{t("openingImport.date")}</FieldLabel>
          <Input id="opi-date" type="date" value={countedDate} disabled={!!busy} onChange={(e) => { setCountedDate(e.target.value); changed(); }} />
        </div>
        <div>
          <FieldLabel htmlFor="opi-reason">{t("openingImport.reason")}</FieldLabel>
          <Input id="opi-reason" value={reason} disabled={!!busy} onChange={(e) => { setReason(e.target.value); if (report) changed(); }} />
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <FieldLabel htmlFor="opi-file">{t("openingImport.file")}</FieldLabel>
          {/* Ô chọn tệp gốc ẩn đi và xoá giá trị sau mỗi lần đọc: sửa Excel rồi chọn lại ĐÚNG tệp đó vẫn đọc lại
              (trình duyệt không báo đổi khi chọn cùng một tệp). Tên tệp hiện riêng bên cạnh. */}
          <div className="flex items-center gap-3">
            <input id="opi-file" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={!!busy || !warehouseId}
              className="peer sr-only"
              onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; void onFile(file); }} />
            <label htmlFor="opi-file"
              className="inline-flex h-9 cursor-pointer items-center rounded-sm border border-line-strong bg-surface px-3.5 text-sm text-ink hover:bg-sunken peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-disabled:cursor-not-allowed peer-disabled:opacity-50">
              {t("openingImport.chooseFile")}
            </label>
            <span className="text-sm text-ink-soft">{fileName || t("openingImport.noFile")}</span>
          </div>
        </div>
        {rows && !report && !busy && <Button size="sm" variant="secondary" onClick={() => void check(rows)}>{t("openingImport.recheck")}</Button>}
        {busy && <span role="status" className="text-sm text-ink-soft">{t(busy === "reading" ? "openingImport.reading" : busy === "checking" ? "openingImport.checking" : "openingImport.creating")}</span>}
      </div>
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      {notice && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{notice}{done && <span className="ml-1 text-ink-soft">{t("openingImport.done")}</span>}</p>}
      {report && (
        <div className="space-y-2">
          <p className="text-sm text-ink">
            <span className="font-medium">{t("openingImport.summary", { ok: report.ok, skipped: report.skipped, errors: report.errors })}</span>
            {sheet && <span className="ml-2 text-xs text-ink-faint">{t("openingImport.sheet", { name: sheet })}</span>}
          </p>
          {report.errors > 0 && <p className="text-sm text-danger">{t("openingImport.fixFirst")}</p>}
          <div className="max-h-96 overflow-auto rounded-md border border-line">
            <TableShell className="rounded-none border-0">
              <thead>
                <tr>
                  <Th align="right">{t("openingImport.columns.row")}</Th>
                  <Th>{t("openingImport.columns.code")}</Th>
                  <Th>{t("openingImport.columns.product")}</Th>
                  <Th>{t("openingImport.columns.warehouse")}</Th>
                  <Th>{t("openingImport.columns.location")}</Th>
                  <Th align="right">{t("openingImport.columns.counted")}</Th>
                  <Th>{t("openingImport.columns.lot")}</Th>
                  <Th>{t("openingImport.columns.status")}</Th>
                </tr>
              </thead>
              <tbody>
                {[...report.rows].sort((a, b) => Number(b.status === "error") - Number(a.status === "error") || a.row - b.row).map((r) => (
                  <Fragment key={r.row}>
                  <Tr className={r.status === "error" ? "bg-danger-tint/60" : undefined}>
                    <Td align="right" className="font-mono tabular text-xs text-ink-faint">{r.row}</Td>
                    <Td className="font-mono tabular text-xs">{r.code}{r.subRef && <span className="text-ink-faint"> / {r.subRef}</span>}</Td>
                    <Td className="max-w-64 truncate text-xs" title={r.description ?? undefined}>{r.description ?? "—"}</Td>
                    <Td className="text-xs">{r.warehouseName ?? "—"}</Td>
                    <Td className="font-mono text-xs">{r.location}</Td>
                    <Td align="right" className="font-mono tabular text-xs">{r.issue !== "ambiguous_count" && /^\d+(\.\d+)?$/.test(r.counted) ? formatQty(Number(r.counted)) : r.counted || "—"} <span className="text-2xs text-ink-faint">{r.unit ?? ""}</span></Td>
                    <Td className="font-mono text-2xs text-ink-soft">{r.lotNumber ?? "—"}</Td>
                    <Td className="text-xs">
                      <Badge tone={r.status === "ok" ? "ok" : r.status === "skipped" ? "neutral" : "danger"}>{t(`openingImport.status.${r.status}`)}</Badge>
                    </Td>
                  </Tr>
                  {/* Lý do lỗi nằm dòng riêng ngay dưới, trải hết bề ngang: tin dài không đẩy bảng tràn ngang. */}
                  {r.issue && (
                    <tr className="bg-danger-tint/60">
                      <td colSpan={8} className="border-b border-line px-3 pb-2 text-xs text-danger">
                        ↳ {t(`openingImport.issues.${r.issue}`, { doc: r.conflictDocumentNo ?? "" })}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </TableShell>
          </div>
          <Button disabled={!ready || !!busy || reason.trim().length < 5} onClick={() => void create()}>
            {busy === "creating" ? t("openingImport.creating") : t("openingImport.create")}
          </Button>
        </div>
      )}
    </section>
  );
}
